// src/pages/ConvertPage.js

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spin, message } from "antd";
import {
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
  ArrowsRightLeftIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { ethers } from "ethers";

import { CHAINS_CONFIG } from "../chains";
import "../components/Walletview.css";

const GOLD_REFERENCE_API =
  "https://goldapi.bait-coin.com/api/gold-reference";
const USERS_API = "https://backend.bait-coin.com/api/users";

const ConvertPage = ({ selectedChain }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { wallet, seedPhrase, baitcashAccount, baitcashBalance } =
    location.state || {};

  // ==========================================================
  // ALL HOOKS MUST BE CALLED AT TOP LEVEL (before early return)
  // ==========================================================
  const [baitTokenBalance, setBaitTokenBalance] = useState(0);
  const [baitTokenBalanceLoading, setBaitTokenBalanceLoading] =
    useState(false);
  const [baitPricePkr, setBaitPricePkr] = useState(null);
  const [pkrRateLoading, setPkrRateLoading] = useState(false);
  const [conversionMode, setConversionMode] = useState("baitToPkr");
  const [conversionAmount, setConversionAmount] = useState("");
  const [conversionLoading, setConversionLoading] = useState(false);
  const [conversionError, setConversionError] = useState("");
  const [conversionStep, setConversionStep] = useState("form");
  const [conversionResult, setConversionResult] = useState(null);

  // ==========================================================
  // FETCH FUNCTIONS
  // ==========================================================
  const fetchBaitBalance = useCallback(async () => {
    if (!wallet || !selectedChain) return;
    try {
      setBaitTokenBalanceLoading(true);
      const chain = CHAINS_CONFIG[selectedChain];
      if (!chain || !chain.rpcUrl)
        throw new Error("RPC not configured");
      const provider = new ethers.JsonRpcProvider(chain.rpcUrl);
      const bal = await provider.getBalance(wallet);
      const nativeBal = Number(ethers.formatEther(bal));
      setBaitTokenBalance(nativeBal);
    } catch (err) {
      console.error("Error fetching BAIT balance:", err);
    } finally {
      setBaitTokenBalanceLoading(false);
    }
  }, [wallet, selectedChain]);

  const fetchRate = useCallback(async () => {
    try {
      setPkrRateLoading(true);
      const response = await fetch(GOLD_REFERENCE_API, {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("Rate API error");
      const data = await response.json();
      if (data?.bait?.price_pkr) {
        setBaitPricePkr(Number(data.bait.price_pkr));
      }
    } catch (err) {
      console.error("Rate fetch error:", err);
    } finally {
      setPkrRateLoading(false);
    }
  }, []);

  // ==========================================================
  // EFFECT: Refresh balances on mount
  // ==========================================================
  useEffect(() => {
    fetchBaitBalance();
    fetchRate();
  }, [fetchBaitBalance, fetchRate]);

  // ==========================================================
  // EARLY RETURN (must be AFTER all hooks)
  // ==========================================================
  if (!wallet || !baitcashAccount) {
    navigate("/dashboard");
    return null;
  }

  // ==========================================================
  // NAVIGATION FUNCTIONS
  // ==========================================================
  const goBack = () => navigate("/dashboard");

  // ==========================================================
  // FORMAT HELPERS
  // ==========================================================
  const formatPkr = (value) => {
    if (!Number.isFinite(Number(value))) return "₨ 0.00";
    return `₨ ${Number(value).toLocaleString("en-PK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatBalance = (bal) => {
    if (bal === undefined || bal === null) return "0.00";
    return bal.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
  };

  // ==========================================================
  // CONVERSION LOGIC
  // ==========================================================
  const getConvertedAmount = () => {
    const value = parseFloat(conversionAmount);
    if (!value || value <= 0 || !baitPricePkr) return 0;
    if (conversionMode === "baitToPkr") {
      return value * baitPricePkr;
    } else {
      return value / baitPricePkr;
    }
  };
  const convertedAmount = getConvertedAmount();

  const validateConversion = () => {
    if (!baitcashAccount) return "Please activate BAITcash first.";
    if (!conversionAmount || conversionAmount.trim() === "")
      return "Please enter an amount.";
    const value = parseFloat(conversionAmount);
    if (!Number.isFinite(value) || value <= 0)
      return "Amount must be greater than zero.";

    if (conversionMode === "baitToPkr") {
      if (value > baitTokenBalance) {
        return `Insufficient BAIT balance. Available: ${formatBalance(
          baitTokenBalance
        )} BAIT`;
      }
      if (value > 1000000)
        return "Maximum 1,000,000 BAIT per transaction.";
    } else {
      if (value > baitcashBalance) {
        return `Insufficient BAITcash balance. Available: ${formatPkr(
          baitcashBalance
        )}`;
      }
      if (value > 100000000)
        return "Maximum 100,000,000 PKR per transaction.";
    }
    return "";
  };

  const handleReviewConversion = async () => {
    await fetchBaitBalance();
    await fetchRate();
    const error = validateConversion();
    if (error) {
      setConversionError(error);
      return;
    }
    setConversionError("");
    setConversionStep("confirm");
  };

  const handleExecuteConversion = async () => {
    setConversionLoading(true);
    setConversionError("");

    try {
      await fetchBaitBalance();
      await fetchRate();

      const value = parseFloat(conversionAmount);
      const currentBaitBalance = baitTokenBalance;
      const currentPKRBalance = baitcashBalance;

      if (!baitcashAccount) {
        setConversionError("Please activate BAITcash first.");
        setConversionLoading(false);
        return;
      }
      if (conversionMode === "baitToPkr") {
        if (value > currentBaitBalance) {
          setConversionError(
            `Insufficient BAIT balance. Available: ${formatBalance(
              currentBaitBalance
            )} BAIT`
          );
          setConversionLoading(false);
          return;
        }
      } else {
        if (value > currentPKRBalance) {
          setConversionError(
            `Insufficient BAITcash balance. Available: ${formatPkr(
              currentPKRBalance
            )}`
          );
          setConversionLoading(false);
          return;
        }
      }

      let endpoint = "";
      let payload = {};

      if (conversionMode === "baitToPkr") {
        if (!seedPhrase)
          throw new Error(
            "Seed phrase missing. Please restart your wallet."
          );
        let cleanSeed = seedPhrase.trim();
        if (cleanSeed.startsWith("0x"))
          cleanSeed = cleanSeed.substring(2);
        const provider = new ethers.JsonRpcProvider(
          "https://rpc.goldchain.tech"
        );
        const walletSigner = ethers.Wallet.fromPhrase(
          cleanSeed,
          provider
        );
        const burnAddress =
          "0x0000000000000000000000000000000000000000";
        const amountWei = ethers.parseEther(value.toString());
        const txRequest = { to: burnAddress, value: amountWei };
        const populatedTx =
          await walletSigner.populateTransaction(txRequest);
        const signedTx = await walletSigner.signTransaction(
          populatedTx
        );

        endpoint = `${USERS_API}/convert/bait-to-pkr`;
        payload = {
          walletAddress: walletSigner.address,
          amountBAIT: value,
          phoneNumber: baitcashAccount?.phoneNumber || "",
          signedTx: signedTx,
        };
      } else {
        endpoint = `${USERS_API}/convert/pkr-to-bait`;
        payload = {
          walletAddress: wallet,
          amountPKR: value,
          phoneNumber: baitcashAccount?.phoneNumber || "",
        };
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Conversion failed");
      }

      let newPKRBalance = currentPKRBalance;
      let newBaitBalance = currentBaitBalance;

      if (data.conversion?.newBalance !== undefined) {
        newPKRBalance = parseFloat(data.conversion.newBalance);
      } else if (conversionMode === "baitToPkr") {
        newPKRBalance = currentPKRBalance + value * baitPricePkr;
      } else {
        newPKRBalance = currentPKRBalance - value;
      }

      if (data.conversion?.newBaitBalance !== undefined) {
        newBaitBalance = parseFloat(
          data.conversion.newBaitBalance
        );
      } else if (conversionMode === "baitToPkr") {
        newBaitBalance = Math.max(0, currentBaitBalance - value);
      } else {
        newBaitBalance = currentBaitBalance + value / baitPricePkr;
      }

      setConversionResult({
        type: conversionMode,
        fromAmount: value,
        fromCurrency:
          conversionMode === "baitToPkr" ? "BAIT" : "PKR",
        toAmount:
          conversionMode === "baitToPkr"
            ? value * baitPricePkr
            : value / baitPricePkr,
        toCurrency:
          conversionMode === "baitToPkr" ? "PKR" : "BAIT",
        rate: baitPricePkr,
        newBalance: newPKRBalance,
        newBaitBalance: newBaitBalance,
        reference:
          data.conversion?.reference || `CONV-${Date.now()}`,
        transactionHash: data.conversion?.transactionHash,
      });

      setConversionStep("success");
      message.success("Conversion successful!");

      await fetchBaitBalance();
    } catch (err) {
      let errorMsg =
        err?.message || "Conversion failed. Please try again.";
      if (errorMsg.includes("insufficient funds")) {
        errorMsg = "Insufficient BAIT balance on the blockchain.";
      } else if (errorMsg.includes("rejected")) {
        errorMsg = "Transaction signing was rejected.";
      } else if (errorMsg.includes("invalid mnemonic")) {
        errorMsg =
          "Invalid seed phrase. Please check your wallet recovery phrase.";
      }
      setConversionError(errorMsg);
    } finally {
      setConversionLoading(false);
    }
  };

  const resetAndGoBack = () => {
    setConversionAmount("");
    setConversionError("");
    setConversionStep("form");
    setConversionResult(null);
    setConversionLoading(false);
    goBack();
  };

  // ==========================================================
  // STYLES
  // ==========================================================
  const styles = {
    page: {
      width: "100%",
      minHeight: "80vh",
      background: "#faf8f5",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      overflowX: "hidden",
    },

    container: {
      width: "calc(100% - 40px)",
      maxWidth: 540,
      margin: "0 auto 32px",
      padding: "0 0 16px",
      boxSizing: "border-box",
    },

    pageTitle: {
      width: "100%",
      marginBottom: 24,
      boxSizing: "border-box",
    },
    titleWrapper: {
      position: "relative",
      width: "100%",
      minHeight: 52,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
    },
    backButton: {
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: 36,
      height: 36,
      padding: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(154,106,24,0.06)",
      border: "1px solid rgba(154,106,24,0.12)",
      borderRadius: "10px",
      color: "#9a6a18",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxSizing: "border-box",
      flexShrink: 0,
    },
    titleContent: {
      width: "100%",
      textAlign: "center",
      padding: "0 48px",
      boxSizing: "border-box",
    },

    // =====================================================
    // TITLE — matches BAIT PAY brand styling
    // =====================================================
    title: {
      margin: "0 0 5px",

      fontFamily:
        "'Inter', 'Poppins', 'Segoe UI', sans-serif",

      fontSize: 26,

      lineHeight: 1.1,

      fontWeight: 600,

      letterSpacing: "1.4px",

      color: "#8a5a1f",

      WebkitTextStroke:
        "0.4px #6b3d0a",

      textShadow:
        "0 1px 0 rgba(255, 240, 200, 0.6), 0 2px 6px rgba(154, 106, 24, 0.22)",
    },

    // =====================================================
    // SUBTITLE — muted bronze
    // =====================================================
    subtitle: {
      margin: "4px 0 0",

      fontFamily:
        "'Poppins', 'Segoe UI', sans-serif",

      fontSize: 11,

      fontWeight: 500,

      letterSpacing: "0.6px",

      lineHeight: 1.3,

      color: "#a07a3f",

      opacity: 0.95,
    },

    formContainer: {
      width: "100%",
      boxSizing: "border-box",
    },
    confirmContainer: {
      width: "100%",
      boxSizing: "border-box",
    },
    successContainer: {
      width: "100%",
      boxSizing: "border-box",
      textAlign: "center",
    },

    sendErrorBox: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: "#fef3e8",
      color: "#9a5b12",
      border: "1px solid #f5d9b8",
      borderRadius: "10px",
      padding: "8px 12px",
      marginBottom: "16px",
      fontSize: "12px",
      fontWeight: 500,
    },

    balanceRow: {
      display: "flex",
      gap: "12px",
      marginBottom: "24px",
    },
    balanceCard: {
      flex: 1,
      background: "#ffffff",
      borderRadius: "14px",
      padding: "16px 18px",
      border: "1px solid #ece4d6",
      boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
    },
    balanceCardLabel: {
      fontSize: "11px",
      fontWeight: 600,
      color: "#92703d",
      letterSpacing: "0.3px",
      textTransform: "uppercase",
      marginBottom: "4px",
    },
    balanceCardAmount: {
      fontSize: "22px",
      fontWeight: 700,
      color: "#2a1f12",
      letterSpacing: "-0.3px",
    },
    balanceCardSub: {
      fontSize: "12px",
      color: "#8a7a62",
      marginTop: "2px",
    },

    modeToggle: {
      display: "flex",
      gap: "8px",
      marginBottom: "20px",
      background: "#ffffff",
      padding: "4px",
      borderRadius: "12px",
      border: "1px solid #ece4d6",
    },
    modeButton: {
      flex: 1,
      padding: "10px 12px",
      border: "none",
      borderRadius: "10px",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
      background: "transparent",
      color: "#5a4a32",
    },
    modeButtonActive: {
      background: "linear-gradient(135deg, #9a6a18, #c18a32)",
      color: "#ffffff",
      boxShadow: "0 2px 8px rgba(154,106,24,0.15)",
    },

    rateDisplay: {
      textAlign: "center",
      fontSize: "14px",
      color: "#8a7a62",
      marginBottom: "20px",
    },

    sendFormSection: {
      marginBottom: "20px",
    },
    sendFormLabel: {
      display: "block",
      fontSize: "13px",
      fontWeight: 600,
      color: "#5a4a32",
      marginBottom: "6px",
    },
    sendAmountInput: {
      width: "100%",
      height: "56px",
      padding: "0 16px",
      borderRadius: "10px",
      border: "1px solid #e0d6c8",
      fontSize: "26px",
      fontWeight: 700,
      color: "#2a1f12",
      background: "#ffffff",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
      outline: "none",
    },
    sendFormHelper: {
      marginTop: "4px",
      fontSize: "11px",
      color: "#9b8b73",
      lineHeight: 1.4,
    },

    convertedDisplay: {
      background: "#f8f4ec",
      borderRadius: "12px",
      padding: "12px 16px",
      marginBottom: "20px",
      textAlign: "center",
      border: "1px solid #ece4d6",
    },
    convertedLabel: {
      fontSize: "12px",
      color: "#8a7a62",
      marginBottom: "4px",
    },
    convertedValue: {
      fontSize: "22px",
      fontWeight: 700,
      color: "#2a1f12",
    },

    sendReviewButton: {
      width: "100%",
      height: "52px",
      borderRadius: "12px",
      border: "none",
      background: "linear-gradient(135deg, #9a6a18, #c18a32)",
      color: "#ffffff",
      fontSize: "15px",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginTop: "8px",
      boxShadow: "0 4px 16px rgba(154,106,24,0.18)",
    },

    securityNote: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      marginTop: "24px",
      padding: "12px 0",
      borderTop: "1px solid #ece4d6",
      fontSize: "12px",
      color: "#9b8b73",
    },
    securityIcon: {
      color: "#9a6a18",
    },

    sendConfirmCard: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: "24px 20px",
      textAlign: "center",
      border: "1px solid #ece4d6",
      boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
      marginBottom: "20px",
    },
    sendConfirmIcon: {
      width: "64px",
      height: "64px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, #f4ead9, #e8dcc8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#9a6a18",
      margin: "0 auto 14px",
    },
    sendConfirmLabel: {
      fontSize: "12px",
      color: "#8a7a62",
      marginBottom: "2px",
    },
    sendConfirmName: {
      fontSize: "18px",
      fontWeight: 700,
      margin: "2px 0",
      color: "#2a1f12",
    },
    sendConfirmPhone: {
      fontSize: "14px",
      color: "#8a7a62",
      marginBottom: "14px",
    },
    sendConfirmAmount: {
      fontSize: "30px",
      fontWeight: 800,
      color: "#2a1f12",
      padding: "10px 0",
      borderTop: "1px solid #ece4d6",
      borderBottom: "1px solid #ece4d6",
    },
    sendRemainingBalance: {
      fontSize: "13px",
      color: "#8a7a62",
      marginTop: "14px",
    },
    sendButtonRow: {
      display: "flex",
      gap: "10px",
      marginTop: "4px",
    },
    sendCancelButton: {
      flex: 1,
      height: "46px",
      borderRadius: "10px",
      border: "1px solid #d5cbbc",
      background: "#ffffff",
      color: "#5a4a32",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    sendConfirmButton: {
      flex: 2,
      height: "46px",
      borderRadius: "10px",
      border: "none",
      background: "linear-gradient(135deg, #9a6a18, #c18a32)",
      color: "#ffffff",
      fontSize: "14px",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 4px 12px rgba(154,106,24,0.16)",
    },

    sendSuccessHeader: {
      textAlign: "center",
      padding: "16px 0 8px",
    },
    sendSuccessAmount: {
      textAlign: "center",
      fontSize: "34px",
      fontWeight: 800,
      color: "#2a1f12",
      padding: "10px 0",
    },
    sendSuccessCard: {
      background: "#f8f4ec",
      borderRadius: "12px",
      padding: "14px 16px",
      margin: "10px 0",
      border: "1px solid #ece4d6",
    },
    sendSuccessRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "6px 0",
      fontSize: "13px",
      color: "#5a4a32",
      borderBottom: "1px solid #ece4d6",
    },
    sendDoneButton: {
      width: "100%",
      marginTop: "16px",
      height: "48px",
      borderRadius: "10px",
      border: "none",
      background: "linear-gradient(135deg, #9a6a18, #c18a32)",
      color: "#ffffff",
      fontSize: "14px",
      fontWeight: 700,
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 4px 12px rgba(154,106,24,0.16)",
    },
  };

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <div style={styles.page}>
      {/* Main */}
      <div style={styles.container}>
        <div style={styles.pageTitle}>
          <div style={styles.titleWrapper}>
            {/* BACK BUTTON — uses shared .market-back-btn styles */}
            <button
              type="button"
              className="market-back-btn"
              onClick={goBack}
              title="Back to Dashboard"
              aria-label="Back to Dashboard"
            >
              <ArrowLeftIcon width={18} height={18} />
            </button>
            <div style={styles.titleContent}>
              <h1 style={styles.title}>Convert</h1>
              <p style={styles.subtitle}>
                Swap between BAIT and PKR instantly
              </p>
            </div>
          </div>
        </div>

        {conversionStep === "success" && conversionResult ? (
          // ---------- SUCCESS ----------
          <div style={styles.successContainer}>
            <div style={styles.sendSuccessHeader}>
              <CheckCircleIcon
                width={60}
                height={60}
                style={{ color: "#4cae5b" }}
              />
              <h2
                style={{
                  margin: "14px 0 2px",
                  fontSize: "22px",
                  fontWeight: 700,
                }}
              >
                Conversion Successful
              </h2>
              <p style={{ fontSize: "13px", color: "#8a7a62" }}>
                {conversionResult.fromCurrency} →{" "}
                {conversionResult.toCurrency}
              </p>
            </div>

            <div style={styles.sendSuccessAmount}>
              {conversionMode === "baitToPkr"
                ? formatPkr(conversionResult.toAmount)
                : `${conversionResult.toAmount.toFixed(6)} BAIT`}
            </div>

            <div style={styles.sendSuccessCard}>
              <div style={styles.sendSuccessRow}>
                <span>Amount Sent</span>
                <strong>
                  {conversionMode === "baitToPkr"
                    ? `${conversionResult.fromAmount.toFixed(
                        4
                      )} BAIT`
                    : formatPkr(conversionResult.fromAmount)}
                </strong>
              </div>
              <div style={styles.sendSuccessRow}>
                <span>Amount Received</span>
                <strong>
                  {conversionMode === "baitToPkr"
                    ? formatPkr(conversionResult.toAmount)
                    : `${conversionResult.toAmount.toFixed(6)} BAIT`}
                </strong>
              </div>
              <div style={styles.sendSuccessRow}>
                <span>Rate</span>
                <strong>
                  1 BAIT = {formatPkr(conversionResult.rate)}
                </strong>
              </div>
              {conversionResult.transactionHash && (
                <div style={styles.sendSuccessRow}>
                  <span>Transaction</span>
                  <strong
                    style={{
                      fontSize: "11px",
                      wordBreak: "break-all",
                    }}
                  >
                    {conversionResult.transactionHash}
                  </strong>
                </div>
              )}
              <div style={styles.sendSuccessRow}>
                <span>Reference</span>
                <strong
                  style={{
                    fontSize: "11px",
                    wordBreak: "break-all",
                  }}
                >
                  {conversionResult.reference}
                </strong>
              </div>
            </div>

            <button
              onClick={resetAndGoBack}
              style={styles.sendDoneButton}
            >
              Done
            </button>
          </div>
        ) : conversionStep === "confirm" ? (
          // ---------- CONFIRM ----------
          <div style={styles.confirmContainer}>
            <div style={styles.sendConfirmCard}>
              <div style={styles.sendConfirmIcon}>
                <ArrowsRightLeftIcon width={44} height={44} />
              </div>
              <p style={styles.sendConfirmLabel}>
                You are converting
              </p>
              <div style={styles.sendConfirmAmount}>
                {conversionMode === "baitToPkr"
                  ? `${parseFloat(conversionAmount).toFixed(
                      4
                    )} BAIT → ${formatPkr(convertedAmount)}`
                  : `${formatPkr(
                      parseFloat(conversionAmount)
                    )} → ${convertedAmount.toFixed(6)} BAIT`}
              </div>
              <div style={styles.sendRemainingBalance}>
                Rate: 1 BAIT = {formatPkr(baitPricePkr)}
              </div>
            </div>

            {conversionError && (
              <div style={styles.sendErrorBox}>
                <InformationCircleIcon width={18} height={18} />
                <span>{conversionError}</span>
              </div>
            )}

            <div style={styles.sendButtonRow}>
              <button
                onClick={() => setConversionStep("form")}
                disabled={conversionLoading}
                style={styles.sendCancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleExecuteConversion}
                disabled={conversionLoading}
                style={styles.sendConfirmButton}
              >
                {conversionLoading ? (
                  <>
                    <Spin size="small" style={{ marginRight: 6 }} />
                    Processing...
                  </>
                ) : (
                  <>
                    Confirm
                    <ArrowRightIcon width={16} height={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          // ---------- FORM ----------
          <div style={styles.formContainer}>
            {conversionError && (
              <div style={styles.sendErrorBox}>
                <InformationCircleIcon width={18} height={18} />
                <span>{conversionError}</span>
              </div>
            )}

            {/* Balance Cards */}
            <div style={styles.balanceRow}>
              <div style={styles.balanceCard}>
                <div style={styles.balanceCardLabel}>
                  BAIT Balance
                </div>
                <div style={styles.balanceCardAmount}>
                  {baitTokenBalanceLoading ? (
                    <Spin size="small" />
                  ) : (
                    `${formatBalance(baitTokenBalance)} BAIT`
                  )}
                </div>
              </div>
              <div style={styles.balanceCard}>
                <div style={styles.balanceCardLabel}>
                  BAITcash Balance
                </div>
                <div style={styles.balanceCardAmount}>
                  {formatPkr(baitcashBalance)}
                </div>
              </div>
            </div>

            {/* Mode Toggle */}
            <div style={styles.modeToggle}>
              <button
                style={{
                  ...styles.modeButton,
                  ...(conversionMode === "baitToPkr"
                    ? styles.modeButtonActive
                    : {}),
                }}
                onClick={() => {
                  setConversionMode("baitToPkr");
                  setConversionAmount("");
                  setConversionError("");
                }}
              >
                BAIT → PKR
              </button>
              <button
                style={{
                  ...styles.modeButton,
                  ...(conversionMode === "pkrToBait"
                    ? styles.modeButtonActive
                    : {}),
                }}
                onClick={() => {
                  setConversionMode("pkrToBait");
                  setConversionAmount("");
                  setConversionError("");
                }}
              >
                PKR → BAIT
              </button>
            </div>

            {/* Rate */}
            <div style={styles.rateDisplay}>
              {pkrRateLoading ? (
                <Spin size="small" />
              ) : (
                `1 BAIT = ${formatPkr(baitPricePkr)}`
              )}
            </div>

            {/* Amount Input */}
            <div style={styles.sendFormSection}>
              <label style={styles.sendFormLabel}>
                Enter{" "}
                {conversionMode === "baitToPkr" ? "BAIT" : "PKR"}{" "}
                Amount
              </label>
              <div>
                <input
                  type="text"
                  inputMode="decimal"
                  value={conversionAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      /^\d*(\.\d{0,4})?$/.test(value)
                    ) {
                      setConversionAmount(value);
                      setConversionError("");
                    }
                  }}
                  placeholder={
                    conversionMode === "baitToPkr"
                      ? "0.0000"
                      : "0.00"
                  }
                  disabled={conversionLoading}
                  style={styles.sendAmountInput}
                />
              </div>
              <div style={styles.sendFormHelper}>
                Available:{" "}
                {conversionMode === "baitToPkr"
                  ? `${formatBalance(baitTokenBalance)} BAIT`
                  : formatPkr(baitcashBalance)}
              </div>
            </div>

            {/* Converted Amount Display */}
            {conversionAmount &&
              parseFloat(conversionAmount) > 0 &&
              baitPricePkr && (
                <div style={styles.convertedDisplay}>
                  <div style={styles.convertedLabel}>
                    You will receive
                  </div>
                  <div style={styles.convertedValue}>
                    {conversionMode === "baitToPkr"
                      ? formatPkr(convertedAmount)
                      : `${convertedAmount.toFixed(6)} BAIT`}
                  </div>
                </div>
              )}

            {/* Review Button */}
            <button
              onClick={handleReviewConversion}
              disabled={
                !conversionAmount ||
                parseFloat(conversionAmount) <= 0 ||
                !baitPricePkr
              }
              style={styles.sendReviewButton}
            >
              Review Conversion
              <ArrowRightIcon width={18} height={18} />
            </button>

            {/* Security Note */}
            <div style={styles.securityNote}>
              <LockClosedIcon
                width={14}
                height={14}
                style={styles.securityIcon}
              />
              <span>Secure • Instant • 24/7</span>
              <span style={{ marginLeft: "auto", opacity: 0.7 }}>
                Your conversions are protected with bank-level
                security
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConvertPage;